import type { VariableType } from 'json-to-graphql-query';
import { PROXY_FORMAT_SLUG, THUMBNAIL_FORMAT_SLUG } from '../constants';
import type { AssetNodeFields, FieldValue } from './types';

export interface AssetNodeTypeResult extends AssetNodeFields {
  myAssetPermissions: FieldValue;
}

export interface AssetNodeTypeNoPermsResult {
  __typeName: 'AssetNodeType';
  id: FieldValue;
  name?: FieldValue;
  clipId?: FieldValue;
  legacySysId?: FieldValue;
  inpoint?: FieldValue;
  outpoint?: FieldValue;
  duration?: FieldValue;
  timecode?: FieldValue;
  language?: FieldValue;
  mediasize?: FieldValue;
  mediatype?: FieldValue;
  jsonMetadata?: FieldValue;
  dateAdded?: FieldValue;
  createdAt?: FieldValue;
  lastModified?: FieldValue;
  customfieldSet: false;
  formatSet: false;
}

export interface AssetNodeTypeSimpleResult {
  __typeName: 'AssetNodeType';
  id: FieldValue;
  myAssetPermissions: FieldValue;
}

export interface AssetQueryResult {
  query: {
    asset: AssetNodeTypeResult & {
      __args: {
        id: string | VariableType;
      };
    };
  };
}

export const AssetNodeTypeNoPerms = (truify: boolean = false): AssetNodeTypeNoPermsResult => ({
  __typeName: 'AssetNodeType',
  id: truify,
  name: true,
  clipId: true,
  legacySysId: truify,
  inpoint: truify,
  outpoint: truify,
  duration: truify,
  timecode: truify,
  language: truify,
  mediasize: true,
  mediatype: true,
  jsonMetadata: truify,
  dateAdded: truify,
  createdAt: truify,
  lastModified: truify,
  customfieldSet: false,
  formatSet: false,
});

export const AssetNodeType = (truify: boolean = false): AssetNodeTypeResult => ({
  ...AssetNodeTypeNoPerms(truify),
  myAssetPermissions: true,
});

export const AssetNodeTypeSimple = (truify: boolean = false): AssetNodeTypeSimpleResult => ({
  __typeName: 'AssetNodeType',
  id: truify,
  myAssetPermissions: truify,
});

export const assetQuery = (): AssetQueryResult => ({
  query: {
    asset: {
      ...AssetNodeType(),
      __args: {
        id: '',
      },
    },
  },
});

export interface CustomFieldSetResult {
  __args: {
    definition_Name_In: string[];
  };
  edges: {
    node: {
      id: FieldValue;
      definition: {
        id: FieldValue;
        name: FieldValue;
        fieldType: FieldValue;
      };
      content: FieldValue;
    };
  };
}

export const customfieldSet = (truify: boolean = false): CustomFieldSetResult => ({
  __args: {
    definition_Name_In: [],
  },
  edges: {
    node: {
      id: truify,
      definition: {
        id: truify,
        name: truify,
        fieldType: truify,
      },
      content: truify,
    },
  },
});

export interface FormatSetResult {
  __args: {
    fmtType_Slug_In: string;
  };
  edges: {
    node: {
      id: FieldValue;
      mediapath: FieldValue;
      filename: FieldValue;
      fmtType: {
        id: FieldValue;
        slug: FieldValue;
        name: FieldValue;
        suffix: FieldValue;
        downloadable: FieldValue;
      };
      online: FieldValue;
      downloadable: FieldValue;
      presignable: FieldValue;
      mimetype: FieldValue;
    };
  };
}

export const formatSet = (truify: boolean = false): FormatSetResult => ({
  __args: {
    fmtType_Slug_In: [THUMBNAIL_FORMAT_SLUG, PROXY_FORMAT_SLUG].join(','),
  },
  edges: {
    node: {
      id: truify,
      mediapath: truify,
      filename: truify,
      fmtType: {
        id: truify,
        slug: truify,
        name: truify,
        suffix: truify,
        downloadable: truify,
      },
      online: truify,
      downloadable: truify,
      presignable: truify,
      mimetype: truify,
    },
  },
});
